/**
 * COPY STRATEGY — WhatIsFinhudasPage
 *
 * Awareness:      Product-Aware (Stage 4/5)
 *                 Reader is evaluating Finhudas specifically. May have seen
 *                 the name in other articles or ads. Needs to understand
 *                 what it does and why it's the best choice.
 *
 * Sophistication: Stage 3 — Mechanism
 *                 Automatic Bolt import as the unique mechanism/USP.
 *                 "Vienintelė programa" — only product with this capability.
 *                 Differentiator: automation vs manual accounting.
 *
 * Avatar:         Driver considering accounting tools or just heard about Finhudas.
 *                 Any experience level. Looking for "finhudas" or
 *                 "apskaitos programa pavežėjams". Ready to evaluate.
 *
 * Hook:           Time = money — "Kiek valandų praleidote skaičiuodami mokesčius?"
 * Structure:      Pain (wasted time) → USP features → How it works → Social proof → CTA
 * Proof:          Feature list with specific capabilities (auto-import, tax calc, journal)
 * Funnel:         Bottom-of-funnel conversion page, direct path to signup
 */
import { motion } from 'framer-motion'
import { PiCheckCircle, PiSparkle } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const WhatIsFinhudasPage = () => {
  usePageSetup('Finhudas: vienintelė programa, automatiškai importuojanti Bolt ataskaitas', {
    scroll: true,
    description: 'Kiek valandų praleidžiate skaičiuodami mokesčius? Finhudas automatiškai importuoja Bolt ataskaitas, skaičiuoja GPM, VSD, PSD ir atvirkštinį PVM. Viskas per 3 minutes.',
    type: 'article',
    datePublished: '2025-10-15',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          Finhudas: vienintelė programa, automatiškai importuojanti Bolt ataskaitas
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Apskaita</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">4 min skaitymo</span>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-4">
          Kiek valandų šį mėnesį praleidote skaičiuodami mokesčius? O kiek galėjote uždirbti tuo metu vairuodami?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-8">
          Profesionalūs pavežėjai žino: laikas = pinigai. Todėl jie naudoja <strong>Finhudas</strong> – vienintelę apskaitos programą, kuri automatiškai importuoja Bolt ataskaitas, skaičiuoja mokesčius ir generuoja apskaitos žurnalą. Jums belieka vairuoti ir uždirbti.
        </MotionText>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kodėl „Finhudas“ yra geriausias pasirinkimas?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-5">
          Tradicinė <strong>individualios veiklos apskaita</strong> reikalauja daug laiko ir pastangų: reikia pildyti <strong>individualios veiklos apskaitos žurnalą</strong>, sekti išlaidas, skaičiuoti mokesčius. „Finhudas“ visa tai automatizuoja.
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-8">
          <div className="p-4">
            <div className="flex gap-3 mb-2">
              <PiSparkle className="min-w-5 h-5 text-zinc-700" />
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium">
                Automatizuota apskaita
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm">
              „Finhudas“ veikia kaip išmani <strong>apskaitos programa</strong>, kuri automatiškai tvarko jūsų pajamas ir išlaidas. Jums nebereikės rankiniu būdu pildyti žurnalų.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <div className="flex gap-3 mb-2">
              <PiSparkle className="min-w-5 h-5 text-zinc-700" />
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium">
                Paprastas mokesčių apskaičiavimas
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Pamirškite sudėtingus skaičiavimus. „Finhudas“ tiksliai apskaičiuoja jūsų <strong>pavežėjo mokesčius</strong> (GPM, PSD, VSD) ir pateikia aiškias ataskaitas.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <div className="flex gap-3 mb-2">
              <PiSparkle className="min-w-5 h-5 text-zinc-700" />
              <MotionText as="h4" className="text-zinc-900 text-sm font-medium">
                Sukurta pavežėjams
              </MotionText>
            </div>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Skirtingai nuo bendrinių <strong>apskaitos programų</strong>, „Finhudas“ yra pritaikytas būtent „Bolt“, „Uber“ ir „Wolt“ vairuotojų poreikiams.
            </MotionText>
          </div>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Pagrindinės „Finhudas“ funkcijos
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionDiv className="space-y-2">
            <MotionDiv className="flex gap-2">
              <PiCheckCircle className="min-w-4 h-4 text-blue-700 mt-0.5" />
              <MotionText as="p" className="text-blue-800 text-sm">
                Automatinis pajamų importas iš „Bolt“ ataskaitų.
              </MotionText>
            </MotionDiv>
            <MotionDiv className="flex gap-2">
              <PiCheckCircle className="min-w-4 h-4 text-blue-700 mt-0.5" />
              <MotionText as="p" className="text-blue-800 text-sm">
                Išlaidų fiksavimas nufotografavus kvitą.
              </MotionText>
            </MotionDiv>
            <MotionDiv className="flex gap-2">
              <PiCheckCircle className="min-w-4 h-4 text-blue-700 mt-0.5" />
              <MotionText as="p" className="text-blue-800 text-sm">
                Realaus laiko mokesčių apskaičiavimas.
              </MotionText>
            </MotionDiv>
            <MotionDiv className="flex gap-2">
              <PiCheckCircle className="min-w-4 h-4 text-blue-700 mt-0.5" />
              <MotionText as="p" className="text-blue-800 text-sm">
                <strong>Individualios veiklos apskaitos žurnalas</strong> generuojamas automatiškai.
              </MotionText>
            </MotionDiv>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-4">
          Pasirinkę „Finhudas“, jūs pasirenkate ramybę ir užtikrintumą, kad jūsų <strong>individualios veiklos apskaita</strong> yra tvarkoma teisingai ir efektyviai. Išbandykite „Finhudas“ ir įsitikinkite patys!
        </MotionText>

        <FinhudasCTA />

        <RelatedArticles articles={[
          { slug: 'bolt-apskaita-dokumentai', title: 'Bolt apskaita: 3 dokumentai, kuriuos PRIVALOTE atsisiųsti' },
          { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM' },
          { slug: 'individualios-veiklos-islaidos-30-procentu-arba-faktines', title: '30% norma ar faktinės išlaidos – kas geriau?' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default WhatIsFinhudasPage
